import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Navbar from "@/components/Navbar";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import ExitIntentOffer from "@/components/ExitIntentOffer";
import ScrollToTop from "@/components/ScrollToTop";
import Analytics from "@/components/Analytics";
import Home from "./pages/Home";
import { useLocation } from "react-router-dom";

/**
 * Every route except the homepage is code-split, so a mobile visitor landing on
 * `/` downloads the hero shell instead of the entire site's JavaScript.
 */
const Services = lazy(() => import("./pages/Services"));
const ForFamilies = lazy(() => import("./pages/ForFamilies"));
const ForSeniors = lazy(() => import("./pages/ForSeniors"));
const ForCaregivers = lazy(() => import("./pages/ForCaregivers"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Resources = lazy(() => import("./pages/Resources"));
const ResourcesLocalHelp = lazy(() => import("./pages/ResourcesLocalHelp"));
const ShopProducts = lazy(() => import("./pages/ShopProducts"));
const ResourcesGuides = lazy(() => import("./pages/ResourcesGuides"));
const ResourcesCaregiverKit = lazy(() => import("./pages/ResourcesCaregiverKit"));
const Article = lazy(() => import("./pages/Article"));
const Neighborhood = lazy(() => import("./pages/Neighborhood"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminAuth = lazy(() => import("./pages/AdminAuth"));
const AdminAssistant = lazy(() => import("./pages/AdminAssistant"));
const AdminTraffic = lazy(() => import("./pages/AdminTraffic"));
const AdminApplications = lazy(() => import("./pages/AdminApplications"));
const AdminSubscribers = lazy(() => import("./pages/AdminSubscribers"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const SeniorCompanionCareNashville = lazy(() => import("./pages/SeniorCompanionCareNashville"));
const CareTopicPage = lazy(() => import("./pages/CareTopicPage"));
const CareRequest = lazy(() => import("./pages/CareRequest"));
const Consultation = lazy(() => import("./pages/Consultation"));
const CaregiverKitShop = lazy(() => import("./pages/CaregiverKitShop"));
const CaregiverKitThankYou = lazy(() => import("./pages/CaregiverKitThankYou"));
const WellnessPlannerShop = lazy(() => import("./pages/WellnessPlannerShop"));
const WellnessPlannerThankYou = lazy(() => import("./pages/WellnessPlannerThankYou"));
const SurvivalGuideShop = lazy(() => import("./pages/SurvivalGuideShop"));
const SurvivalGuideThankYou = lazy(() => import("./pages/SurvivalGuideThankYou"));
const Shop = lazy(() => import("./pages/Shop"));
const CaregiverBurnoutCheck = lazy(() => import("./pages/CaregiverBurnoutCheck"));
const TimeForHelp = lazy(() => import("./pages/TimeForHelp"));
const HiringChecklist = lazy(() => import("./pages/HiringChecklist"));
const EmergencyWalletCard = lazy(() => import("./pages/EmergencyWalletCard"));
const FreeResources = lazy(() => import("./pages/FreeResources"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const NewsletterConfirm = lazy(() => import("./pages/NewsletterConfirm"));
const CourseConfirm = lazy(() => import("./pages/CourseConfirm"));
const Refer = lazy(() => import("./pages/Refer"));
const Partners = lazy(() => import("./pages/Partners"));
const ReviewUs = lazy(() => import("./pages/ReviewUs"));
const FiveQuestions = lazy(() => import("./pages/FiveQuestions"));
const ShareExperience = lazy(() => import("./pages/ShareExperience"));
const GuideHubPage = lazy(() => import("./pages/GuideHub"));
const CareerPortal = lazy(() => import("./pages/CareerPortal"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ContactChat = lazy(() => import("@/components/ContactChat"));

const queryClient = new QueryClient();

const RouteFallback = () => <div className="min-h-[60vh]" aria-busy="true" aria-live="polite" />;

const Chrome = ({ children }: { children: React.ReactNode }) => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <>
      {!isAdmin && (
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-3 focus:text-base focus:font-medium focus:text-primary-foreground"
        >
          Skip to main content
        </a>
      )}
      {!isAdmin && <Navbar />}
      {!isAdmin && <Breadcrumbs />}

      <main id="main-content" tabIndex={-1} className="focus:outline-none">
        {children}
      </main>
      {!isAdmin && <Footer />}
      {!isAdmin && <StickyMobileCTA />}
      {!isAdmin && <ExitIntentOffer />}
      {!isAdmin && (
        <Suspense fallback={null}>
          <ContactChat />
        </Suspense>
      )}
    </>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Analytics />
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground focus:shadow-lg"
          >
            Skip to main content
          </a>
          <Chrome>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/services" element={<Services />} />
                <Route path="/for-families" element={<ForFamilies />} />
                <Route path="/for-seniors" element={<ForSeniors />} />
                <Route path="/for-caregivers" element={<ForCaregivers />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/resources/local-help" element={<ResourcesLocalHelp />} />
                <Route path="/resources/guides" element={<ResourcesGuides />} />
                <Route path="/guides/:slug" element={<GuideHubPage />} />
                <Route path="/resources/caregiver-kit" element={<ResourcesCaregiverKit />} />
                <Route path="/resources/:slug" element={<Article />} />
                <Route path="/areas/:slug" element={<Neighborhood />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/newsletter/confirm" element={<NewsletterConfirm />} />
                <Route path="/course/confirm" element={<CourseConfirm />} />
                <Route path="/contact/thank-you" element={<ThankYou />} />
                <Route path="/for-families/thank-you" element={<ThankYou />} />
                <Route path="/for-caregivers/thank-you" element={<ThankYou />} />
                <Route path="/careers/portal" element={<CareerPortal />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/admin/auth" element={<AdminAuth />} />
                <Route path="/admin/assistant" element={<AdminAssistant />} />
                <Route path="/admin/traffic" element={<AdminTraffic />} />
                <Route path="/admin/applications" element={<AdminApplications />} />
                <Route path="/admin/subscribers" element={<AdminSubscribers />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/senior-companion-care-nashville" element={<SeniorCompanionCareNashville />} />
                <Route path="/senior-care-nashville" element={<CareTopicPage />} />
                <Route path="/home-care-nashville" element={<CareTopicPage />} />
                <Route path="/elder-care-nashville" element={<CareTopicPage />} />
                <Route path="/respite-care-nashville" element={<CareTopicPage />} />
                <Route
                  path="/nashville-senior-care"
                  element={<Navigate to="/senior-companion-care-nashville" replace />}
                />
                <Route path="/consultation" element={<Consultation />} />
                <Route path="/care-request" element={<CareRequest />} />
                <Route path="/refer" element={<Refer />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/for-referral-partners" element={<Navigate to="/partners" replace />} />
                <Route path="/review" element={<ReviewUs />} />
                <Route path="/guides/5-questions" element={<FiveQuestions />} />
                <Route path="/share-your-experience" element={<ShareExperience />} />
                <Route path="/tools/caregiver-burnout-check" element={<CaregiverBurnoutCheck />} />
                <Route path="/caregiver-burnout-test" element={<Navigate to="/tools/caregiver-burnout-check" replace />} />
                <Route path="/tools/is-it-time-for-help" element={<TimeForHelp />} />
                <Route path="/tools/hiring-checklist" element={<HiringChecklist />} />
                <Route path="/tools/emergency-wallet-card" element={<EmergencyWalletCard />} />
                <Route path="/free-resources" element={<FreeResources />} />
                <Route path="/resources/free" element={<Navigate to="/free-resources" replace />} />

                <Route path="/shop" element={<Shop />} />
                <Route path="/shop/products" element={<ShopProducts />} />

                <Route path="/resources/products" element={<Navigate to="/shop/products" replace />} />
                <Route path="/shop/caregiver-kit" element={<CaregiverKitShop />} />
                <Route path="/shop/caregiver-kit/thank-you" element={<CaregiverKitThankYou />} />
                <Route path="/shop/wellness-planner" element={<WellnessPlannerShop />} />
                <Route path="/shop/book" element={<SurvivalGuideShop />} />
                <Route path="/shop/book/thank-you" element={<SurvivalGuideThankYou />} />
                <Route path="/survival-guide" element={<Navigate to="/shop/book" replace />} />
                <Route path="/book" element={<Navigate to="/shop/book" replace />} />
                <Route path="/shop/wellness-planner/thank-you" element={<WellnessPlannerThankYou />} />
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </Chrome>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
